import Cookies from 'js-cookie'
import { generateID, globalState } from './utils'
import type {
  StorageItem,
  SessionKeyDefinitions,
  StorageItemValue
} from './session.types'

/**
 * Base class for managing values in both SessionStorage and Cookies
 */
class StorageValue implements StorageItem {
  protected key: string
  protected expires: number

  constructor(key: string, expires = 0) {
    this.key = key
    this.expires = expires
  }

  get(delimiter = ''): string | string[] | null {
    // Try to get value from Cookie first, then SessionStorage
    const value = Cookies.get(this.key) || sessionStorage.getItem(this.key)
    if (typeof value === 'string' && delimiter) {
      return value.split(delimiter)
    }
    return value
  }

  set(value: StorageItemValue): StorageItemValue {
    if (!value) {
      Cookies.remove(this.key)
      sessionStorage.removeItem(this.key)
    } else {
      const stringValue = typeof value === 'string' ? value : JSON.stringify(value)
      Cookies.set(this.key, stringValue, { expires: this.expires })
      sessionStorage.setItem(this.key, stringValue)
    }
    return value
  }
}

/**
 * Class for managing ID values with automatic generation
 */
class StorageID extends StorageValue {
  constructor(key: string, expires = 0) {
    super(key, expires)
    this.get() // Initialize ID if not exists
  }

  get(): string {
    let id = super.get() as string | null
    if (!id) {
      id = super.set(this.generate()) as string
    }
    return id
  }

  protected generate(): string {
    return generateID()
  }
}

/**
 * Main Session class for managing various IDs and values
 */
class Session {
  [key: string]: StorageItem | (() => void); // Allow dynamic properties while maintaining type safety

  static readonly KEY_DEFINITIONS: SessionKeyDefinitions = {
    pageLoadId: { id: 'ssPageLoadId', expires: 0, generateIfEmpty: true },
    shopper: { id: 'ssShopperId', expires: 365, generateIfEmpty: false },
    sessionId: { id: 'ssSessionIdNamespace', expires: 0, generateIfEmpty: true },
    userId: { id: 'ssUserId', expires: 365, generateIfEmpty: true },
    isuid: { id: '_isuid', expires: 365, generateIfEmpty: false },
    viewedProducts: {
      id: 'ssViewedProducts',
      expires: 365 * 7,
      delimiter: ',',
      generateIfEmpty: false
    },
    columnPreferences: {
      id: 'ssColumnPreferences',
      expires: 0,
      generateIfEmpty: false
    }
  }

  pageLoadId!: StorageID
  shopper!: StorageValue
  sessionId!: StorageID
  userId!: StorageID
  isuid!: StorageValue
  viewedProducts!: StorageValue
  columnPreferences!: StorageValue

  constructor() {
    this.initializeStorageItems()
    this.setUserID()
    this.setShopperID()
  }

  protected initializeStorageItems(): void {
    for (const [key, value] of Object.entries(Session.KEY_DEFINITIONS)) {
      const StorageClass = value.generateIfEmpty ? StorageID : StorageValue
      this[key] = new StorageClass(value.id, value.expires)
      if (value.generateIfEmpty) {
        (this[key] as StorageID).get() // This will trigger ID generation if it doesn't exist
      }
    }
  }

  protected setUserID(): void {
    const userId = this.userId.get()
    this.isuid.set(userId) // Set _isuid to match ssUserId
  }

  protected setShopperID(): void {
    const customerID = globalState?.customer?.id

    if (customerID) {
      this.shopper.set(customerID)
    } else {
      // delete shopper if customer is not logged in
      this.shopper.set(null)
    }
  }
}

// Export a singleton instance
export const session = new Session()
